import { useEffect } from 'react';

import { useLocalStorage } from '@mantine/hooks';

export type ExpiringLocalStorageData<T> = {
    value: T;
    expiresAt: number;
};

type Props<T> = {
    key: string;
    defaultValue?: T | null;
    timeToLive: number; // in ms
};

type ReturnType<T> = [T | null, (value: T, timeToLiveOverride?: number) => void];

const useExpiringLocalStorage = <T>({ key, defaultValue = null, timeToLive }: Props<T>): ReturnType<T> => {
    const [raw, setRaw] = useLocalStorage<ExpiringLocalStorageData<T> | null>({
        key,
        defaultValue: null,
    });

    const isExpired = raw?.expiresAt && Date.now() > raw.expiresAt;

    const setValue = (newValue: T, timeToLiveOverride?: number) => {
        const ttl = timeToLiveOverride ?? timeToLive;

        setRaw({
            value: newValue,
            expiresAt: Date.now() + ttl,
        });
    };

    const clear = () => setRaw(null);

    useEffect(() => {
        if (raw && isExpired) {
            clear();
        }
    }, [raw]);

    const value = !isExpired && raw ? raw.value : defaultValue;

    return [value, setValue];
};

export { useExpiringLocalStorage };
